import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    LayoutDashboard,
    Wallet,
    PlayCircle,
    FileText,
    CheckSquare
} from 'lucide-react';

import ModuleLayout from '../../../layouts/ModuleLayout';
import Drawer from '../../../components/common/Drawer';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { payslipsApi } from '../../../api/payslipsApi';

// Import Payroll Components
import PayrollDashboard from './PayrollDashboard';
import SalaryStructures from './SalaryStructures';
import CreateSalaryStructure from './CreateSalaryStructure';
import PayrollRun from './PayrollRun';
import PayslipManagement from './PayslipManagement';
import PayslipBatchDetails from './PayslipBatchDetails';
import PayslipDetail from './PayslipDetail';
import PayrollRunDetails from './PayrollRunDetails';
import GeneratePayslips from './GeneratePayslips';
import DistributionLogs from './DistributionLogs';
import PayslipDetailsPage from './PayslipDetailsPage';

const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

const PayrollModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // Define Tabs
    const tabs = [
        { id: 'payrollDashboard', label: 'Payroll Dashboard', icon: LayoutDashboard },
        { id: 'salaryStructure', label: 'Salary Structures', icon: Wallet },
        { id: 'payrollRun', label: 'Payroll Runs', icon: PlayCircle },
        { id: 'payslips', label: 'Payslips', icon: FileText },
        { id: 'distributionLogs', label: 'Distribution Logs', icon: CheckSquare },
    ];

    // Determine active tab from URL or Path
    const getTabFromPath = () => {
        const path = location.pathname;
        if (path.includes('/payroll_dashboard') || path === '/payroll' || path === '/hrms/payroll') return 'payrollDashboard';
        if (path.includes('/salary_structure')) return 'salaryStructure';
        if (path.includes('/payroll_run')) return 'payrollRun';
        if (path.includes('/payslips')) return 'payslips';
        if (path.includes('/distribution_logs')) return 'distributionLogs';
        return 'payrollDashboard';
    };

    const initialTab = searchParams.get('tab') || getTabFromPath();
    const [activeTab, setActiveTab] = useState(initialTab);

    // Sync tab state with URL
    useEffect(() => {
        const currentTabIcon = searchParams.get('tab');
        if (currentTabIcon && currentTabIcon !== activeTab) {
            setActiveTab(currentTabIcon);
        } else if (!currentTabIcon) {
            // If no tab param, set it based on current path but don't force push if it's already correct match
            const pathTab = getTabFromPath();
            if (pathTab !== activeTab) {
                setActiveTab(pathTab);
            }
        }
    }, [searchParams, location.pathname]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);

        // Define path mapping to navigate to tab base routes
        const pathMap = {
            'payrollDashboard': '/payroll_dashboard',
            'salaryStructure': '/salary_structure',
            'payrollRun': '/payroll_run',
            'payslips': '/payslips',
            'distributionLogs': '/distribution_logs'
        };

        if (pathMap[tabId]) {
            navigate(pathMap[tabId]);
        } else {
            setSearchParams({ tab: tabId });
        }
    };

    // Drawer / Action State Management
    const action = searchParams.get('action');
    const actionId = searchParams.get('id') || params.batchId; // support route param batchId too if needed

    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        newParams.delete('structureId');

        let pathname = location.pathname;
        if (pathname.includes('/salary_structure/create')) {
            pathname = '/salary_structure';
        }

        navigate({ pathname, search: newParams.toString() });
    };

    // Handlers to open Drawers
    const handleAddSalaryStructure = () => {
        setSearchParams({ tab: 'salaryStructure', action: 'createSalaryStructure' });
    };

    const handleEditSalaryStructure = (id) => {
        setSearchParams({ tab: 'salaryStructure', action: 'createSalaryStructure', id });
    };

    const handleViewPayslip = (id) => {
        navigate(`/payroll/payslips/${id}`);
    }

    // Render Logic
    const renderTabContent = () => {
        switch (activeTab) {
            case 'payrollDashboard':
                return <PayrollDashboard />;
            case 'salaryStructure':
                if (action === 'createSalaryStructure' || location.pathname.includes('/salary_structure/create')) {
                    return (
                        <PermissionGate permission="payroll" action={actionId ? "update" : "create"} showFallback fallback={<AccessDenied />}>
                            <CreateSalaryStructure
                                id={actionId || searchParams.get('structureId')}
                                onClose={closeDrawer}
                                onSuccess={closeDrawer}
                            />
                        </PermissionGate>
                    );
                }
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <SalaryStructures
                            onAdd={handleAddSalaryStructure}
                            onEdit={handleEditSalaryStructure}
                        />
                    </PermissionGate>
                );
            case 'payrollRun':
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <PayrollRun />
                    </PermissionGate>
                );
            case 'payslips':
                if (action === 'generatePayslips') {
                    return (
                        <PermissionGate permission="payroll" action="create" showFallback fallback={<AccessDenied />}>
                            <GeneratePayslips />
                        </PermissionGate>
                    );
                }
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <PayslipManagement onViewPayslip={handleViewPayslip} />
                    </PermissionGate>
                );
            case 'distributionLogs':
                return (
                    <PermissionGate permission="payroll" action="read" showFallback fallback={<AccessDenied />}>
                        <DistributionLogs />
                    </PermissionGate>
                );

            default:
                return <PayrollDashboard />;
        }
    };

    const isBatchRoute = location.pathname.includes('/payslips/batch/');
    const isRunRoute = location.pathname.includes('/payroll_run/') && params.runId;
    const isPayslipDetailRoute = location.pathname.includes('/payroll/payslips/') && params.id;

    return (
        <ModuleLayout
            title="Payroll Management"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
        >
            {/* Main Content */}
            {!isBatchRoute && !isRunRoute && !isPayslipDetailRoute && renderTabContent()}

            {isBatchRoute && (
                <div className="p-6">
                    <PayslipBatchDetails />
                </div>
            )}

            {isRunRoute && (
                <div className="p-6">
                    <PayrollRunDetails runId={params.runId} />
                </div>
            )}

            {isPayslipDetailRoute && (
                <div className="">
                    <PayslipDetailsPage />
                </div>
            )}

            {/* Drawers */}




        </ModuleLayout>
    );
};


export default PayrollModule;
